import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Pencil, Check, X, Plus, Trash2, Star, MapPin, Phone, Mail, User as UserIcon, LogOut, Loader2,
} from "lucide-react";
import AddressModal, { EMPTY_ADDRESS, validateAddress } from "../components/AddressModal";
import ConfirmModal from "../components/ConfirmModal";
import SEOHead from "../components/SEOHead";

const STATES_UNUSED = null; // states moved to AddressModal

export default function Account() {
  const { user, setUser, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [addrModal, setAddrModal] = useState({ open: false, mode: "add", data: EMPTY_ADDRESS });
  const [actionsFor, setActionsFor] = useState(null);
  const [logoutOpen, setLogoutOpen] = useState(false);

  // Auth gate
  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  // Load orders + addresses
  const refreshAddresses = useCallback(async () => {
    try {
      const r = await api.get("/auth/addresses");
      setAddresses(r.data || []);
    } catch {
      // Address book is optional UX — keep the empty list and let the user
      // re-try via the Add Address form. No user-facing toast needed.
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshAddresses();
  }, [user, refreshAddresses]);

  if (!user) return null;
  const firstName = (user.name || "").split(" ")[0] || "there";

  // ---- Name edit ----
  const startEditName = () => { setNameDraft(user.name || ""); setEditingName(true); };
  const cancelEditName = () => { setEditingName(false); setNameDraft(""); };
  const saveName = async () => {
    const name = nameDraft.trim();
    if (!name) { toast.error("Name cannot be empty"); return; }
    setSavingName(true);
    try {
      const r = await api.put("/auth/me", { name });
      setUser?.(r.data);
      setEditingName(false);
      toast.success("Name updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingName(false);
    }
  };

  // ---- Address CRUD ----
  const openAddAddress = () => setAddrModal({ open: true, mode: "add", data: { ...EMPTY_ADDRESS, full_name: user.name || "", phone: user.phone || "" } });
  const openEditAddress = (a) => { setActionsFor(null); setAddrModal({ open: true, mode: "edit", data: { ...a } }); };
  const closeAddrModal = () => setAddrModal({ open: false, mode: "add", data: EMPTY_ADDRESS });

  const saveAddress = async () => {
    const a = addrModal.data;
    const err = validateAddress(a);
    if (err) { toast.error(err); return; }
    try {
      if (addrModal.mode === "add") {
        const r = await api.post("/auth/addresses", a);
        setAddresses(r.data);
        toast.success("Address added");
      } else {
        const r = await api.put(`/auth/addresses/${a.address_id}`, a);
        setAddresses(r.data);
        toast.success("Address updated");
      }
      closeAddrModal();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setDefault = async (addr) => {
    try {
      const r = await api.put(`/auth/addresses/${addr.address_id}`, { is_default: true });
      setAddresses(r.data);
      setActionsFor(null);
      toast.success("Default address updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteAddress = async (addr) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      const r = await api.delete(`/auth/addresses/${addr.address_id}`);
      setAddresses(r.data);
      setActionsFor(null);
      toast.success("Address deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="container-x py-12 lg:py-20 max-w-5xl" data-testid="account-page">
      <SEOHead title="My Account | Sri Durga Foods" description="Manage your profile, saved addresses, and order history at Sri Durga Foods." noindex />
      {/* Heading + greeting */}
      <div className="mb-10">
        <span className="overline mb-3 block" data-testid="account-overline">My Account</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-dark" data-testid="account-greeting">
          Hello, {firstName}.
        </h1>
      </div>

      {/* Customer Info Card */}
      <section
        className="bg-white border border-brand-line rounded-2xl p-6 sm:p-8 mb-8"
        data-testid="account-info-card"
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <h2 className="font-serif text-2xl text-brand-dark">Customer information</h2>
          <button
            type="button"
            onClick={() => setLogoutOpen(true)}
            className="text-sm text-brand-muted flex items-center gap-2 active:text-brand-chili transition-colors"
            data-testid="account-logout"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-8">
          {/* Name (editable) */}
          <div>
            <dt className="overline !text-brand-muted text-[10px] mb-1.5 flex items-center gap-1.5">
              <UserIcon className="w-3.5 h-3.5" strokeWidth={1.7} /> Name
            </dt>
            {editingName ? (
              <div className="flex items-center gap-2">
                <input
                  className="input-field !py-2 flex-1"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  autoFocus
                  data-testid="name-input"
                />
                <button
                  type="button"
                  onClick={saveName}
                  disabled={savingName}
                  aria-label="Save name"
                  className="w-9 h-9 rounded-full bg-brand-chili text-white flex items-center justify-center disabled:opacity-50"
                  data-testid="name-save"
                >
                  {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={cancelEditName}
                  aria-label="Cancel"
                  className="w-9 h-9 rounded-full bg-brand-subtle text-brand-dark flex items-center justify-center"
                  data-testid="name-cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <dd className="flex items-center gap-2">
                <span className="text-brand-dark text-base" data-testid="name-display">{user.name || "—"}</span>
                <button
                  type="button"
                  onClick={startEditName}
                  aria-label="Edit name"
                  className="w-7 h-7 rounded-full text-brand-muted active:text-brand-chili active:bg-brand-subtle flex items-center justify-center transition-colors"
                  data-testid="name-edit-btn"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </dd>
            )}
          </div>

          <div>
            <dt className="overline !text-brand-muted text-[10px] mb-1.5 flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" strokeWidth={1.7} /> Phone
            </dt>
            <dd className="text-brand-dark text-base" data-testid="phone-display">
              {user.phone || <span className="text-brand-muted italic">Not added</span>}
            </dd>
          </div>

          <div className="sm:col-span-2">
            <dt className="overline !text-brand-muted text-[10px] mb-1.5 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" strokeWidth={1.7} /> Email
            </dt>
            <dd className="text-brand-dark text-base break-all" data-testid="email-display">{user.email}</dd>
          </div>
        </dl>
      </section>

      {/* Saved Addresses */}
      <section className="mb-12" data-testid="saved-addresses-section">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <h2 className="font-serif text-2xl text-brand-dark">Saved addresses</h2>
          <button
            type="button"
            onClick={openAddAddress}
            className="btn-primary !py-2.5 !px-5 !text-sm"
            data-testid="add-address-btn"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={2} /> Add new address
          </button>
        </div>

        {addresses.length === 0 ? (
          <div className="bg-white border border-dashed border-brand-line rounded-2xl p-10 text-center" data-testid="no-addresses">
            <MapPin className="w-6 h-6 mx-auto text-brand-muted mb-3" strokeWidth={1.5} />
            <p className="text-brand-muted text-sm mb-4">No saved addresses yet. Add one to speed up checkout.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="address-list">
            {addresses.map((a) => (
              <article
                key={a.address_id}
                className={`relative bg-white border rounded-2xl p-5 transition-colors ${a.is_default ? "border-brand-chili/40 ring-1 ring-brand-chili/20" : "border-brand-line"}`}
                data-testid={`address-card-${a.address_id}`}
              >
                {a.is_default && (
                  <span
                    className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 bg-brand-chili text-white rounded-full"
                    data-testid={`address-default-badge-${a.address_id}`}
                  >
                    <Star className="w-3 h-3 fill-current" strokeWidth={0} /> Default
                  </span>
                )}
                <div className="font-serif text-lg text-brand-dark mb-1">{a.full_name}</div>
                <div className="text-sm text-brand-muted mb-3">{a.phone}</div>
                <address className="not-italic text-sm text-brand-dark leading-relaxed">
                  {a.house_no}
                  {a.street ? `, ${a.street}` : ""}
                  {a.landmark ? ` · ${a.landmark}` : ""}
                  <br />
                  {a.city}, {a.state}
                  <br />
                  <span className="font-medium">PIN {a.pincode}</span>
                </address>

                <div className="mt-5 flex items-center justify-end relative">
                  <button
                    type="button"
                    onClick={() => setActionsFor(actionsFor === a.address_id ? null : a.address_id)}
                    aria-label="Address actions"
                    className="inline-flex items-center gap-1.5 text-xs text-brand-muted active:text-brand-chili px-3 py-2 rounded-full active:bg-brand-subtle transition-colors"
                    data-testid={`address-edit-${a.address_id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>

                  {actionsFor === a.address_id && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setActionsFor(null)} />
                      <div
                        className="absolute right-0 bottom-12 w-56 bg-white border border-brand-line rounded-xl shadow-lg z-40 overflow-hidden"
                        data-testid={`address-menu-${a.address_id}`}
                      >
                        <button
                          type="button"
                          onClick={() => openEditAddress(a)}
                          className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-brand-dark text-left hover:bg-brand-subtle active:bg-brand-subtle"
                          data-testid={`menu-edit-${a.address_id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit address
                        </button>
                        {!a.is_default && (
                          <button
                            type="button"
                            onClick={() => setDefault(a)}
                            className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-brand-dark text-left hover:bg-brand-subtle active:bg-brand-subtle"
                            data-testid={`menu-default-${a.address_id}`}
                          >
                            <Star className="w-3.5 h-3.5" /> Set as default
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteAddress(a)}
                          className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-brand-chili text-left hover:bg-brand-subtle active:bg-brand-subtle border-t border-brand-line"
                          data-testid={`menu-delete-${a.address_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete address
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Address Modal */}
      {addrModal.open && (
        <AddressModal
          mode={addrModal.mode}
          data={addrModal.data}
          onChange={(d) => setAddrModal((m) => ({ ...m, data: d }))}
          onClose={closeAddrModal}
          onSave={saveAddress}
        />
      )}

      <ConfirmModal
        open={logoutOpen}
        title="Sign out?"
        message="You'll be signed out of your Sri Durga Foods account on this device."
        confirmLabel="Sign out"
        cancelLabel="Cancel"
        onClose={() => setLogoutOpen(false)}
        onConfirm={() => { setLogoutOpen(false); logout(); navigate("/"); }}
        dataTestId="logout-confirm"
      />
    </div>
  );
}
